<template>
  <div class="flex shrink-0 grow basis-0 items-end space-x-1">
    <div class="flex-1">
      <label v-if="label" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {{ label }}
      </label>
      <div class="flex items-center space-x-2">
        <span class="text-sm text-gray-600 dark:text-gray-400">
          {{ displayText }}
        </span>
        <input ref="fileInput" :accept="accept" class="hidden" type="file" @change="handleFileChange" />

        <button
          v-if="hasFile && (hasFileContents || showRemoteDownloadButton)"
          aria-label="Download"
          class="inline-flex h-8 w-8 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
          type="button"
          @click="handleDownloadFile"
        >
          <ArrowDownTrayIcon class="h-4 w-4 cursor-pointer" />
        </button>

        <button
          v-if="hasFile"
          :aria-label="deleteAriaLabel"
          class="inline-flex h-8 w-8 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
          type="button"
          @click="handleRemoveFile"
        >
          <TrashIcon class="h-4 w-4" />
        </button>
        <button
          class="inline-flex h-8 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-3 py-1 text-xs font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
          type="button"
          @click="$refs.fileInput.click()"
        >
          {{ currentButtonText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { ArrowDownTrayIcon, TrashIcon } from "@heroicons/vue/24/outline";

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      file: null,
      removeExistingFile: false,
    }),
  },
  label: {
    type: String,
    default: "",
  },
  showRemoteDownloadButton: {
    type: Boolean,
    default: false,
  },
  accept: {
    type: String,
    default: "*",
  },
  selectButtonText: {
    type: String,
    default: "Select File",
  },
  changeButtonText: {
    type: String,
    default: "Change File",
  },
  noFileText: {
    type: String,
    default: "No file selected",
  },
  existingFileText: {
    type: String,
    default: "File provided",
  },
  deleteAriaLabel: {
    type: String,
    default: "Delete file",
  },
});

const normalizedModelValue = computed(() => {
  return (
    props.modelValue ?? {
      file: null,
      removeExistingFile: false,
    }
  );
});

const emit = defineEmits(["update:modelValue", "download-requested"]);

const fileInput = ref(null);

const hasFile = computed(() => {
  return !!normalizedModelValue.value.file && !normalizedModelValue.value.removeExistingFile;
});

const hasFileContents = computed(() => {
  return (
    hasFile.value &&
    normalizedModelValue.value.file?.contents !== "" &&
    normalizedModelValue.value.file?.contents?.length > 0
  );
});

const displayText = computed(() => {
  if (hasFile.value) {
    return normalizedModelValue.value.file?.name ?? props.existingFileText;
  }
  return props.noFileText;
});

const currentButtonText = computed(() => {
  return hasFile.value ? props.changeButtonText : props.selectButtonText;
});

function handleDownloadFile() {
  if (!hasFileContents.value && props.showRemoteDownloadButton) {
    emit("download-requested");
    return;
  }

  const blob = new Blob([atob(normalizedModelValue.value.file?.contents)], {
    type: normalizedModelValue.value.file?.type,
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = normalizedModelValue.value.file?.name ?? "userFile";
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

async function handleFileChange(event) {
  const file = event.target.files[0];
  if (file) {
    emit("update:modelValue", {
      file: { name: file.name, type: file.type, contents: await readFileAsDataUrl(file) },
      removeExistingFile: false,
    });
  }
}

function handleRemoveFile() {
  emit("update:modelValue", {
    file: null,
    removeExistingFile: true,
  });

  if (fileInput.value) {
    fileInput.value = "";
  }
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Watch for external changes to reset the file input
watch(
  () => normalizedModelValue.value,
  (newValue) => {
    if (!newValue.file && fileInput.value) {
      fileInput.value = "";
    }
  },
);
</script>
